import { useTranslate } from '@tolgee/react';
import Sider from 'antd/es/layout/Sider';
import classNames from 'classnames';
import { useContext, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGlobalActions,
  useGlobalContext,
} from '../../../global-context/GlobalContext';
import useIsMobile from '../../../hooks/utils/useIsMobile';
import useOutsideClick from '../../../hooks/utils/useOutsideClick';
import IUserMenu from '../../../interfaces/IUserMenu';
import { LINKS } from '../../../routes/Links';
import { AccessContext } from '../../../store/access/AccessContext';
import findMenuByField from '../../../utils/findMenuByField';
import getMenus from '../../../utils/getMenus';
import resolveMenuPath from '../../../utils/resolveMenuPath';
import { FeelEatMenu } from '../../feel-eat/feel-eat-menu/FeelEatMenu';
import { sidebarBottomMenu } from '../side-menu/sideMenus';
import './MainNavigation.scss';

export const MainNavigation = ({ isSignedIn }: { isSignedIn: boolean }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  const access = useContext(AccessContext);
  const { clearUserSession, setIsSidePanelOpen } = useGlobalActions();
  const isSidePanelOpen = useGlobalContext(c => c.app.isSidePanelOpen);
  const globalMainMenus: IUserMenu[] = useGlobalContext(data => data.mainMenus);
  const globalRolePermissions = useGlobalContext(data => data.rolePermissions);
  const { t } = useTranslate();

  const mainMenus = (access?.menus && access.menus.length > 0) ? access.menus : globalMainMenus;
  const rolePermissions = (access?.rolePermissions && access.rolePermissions.length > 0) ? access.rolePermissions : globalRolePermissions;

  const menus = useMemo(
    () => getMenus(mainMenus || [], rolePermissions || []),
    [mainMenus, rolePermissions]
  );

  useOutsideClick(ref, () => {
    setTimeout(() => {
      setIsSidePanelOpen(false);
    }, 200);
  });

  const selectedKey = useMemo(() => {
    const currentPath = location.pathname;
    const menuItem = findMenuByField(mainMenus || [], 'path', currentPath);
    return [menuItem ? menuItem.id.toString() : '1'];
  }, [location.pathname, mainMenus]);

  const sideBarBottom = sidebarBottomMenu();

  const navigateToPage = (item: any) => {
    const menuItem = findMenuByField(mainMenus || [], 'id', item.key);
    const path = resolveMenuPath(menuItem);

    if (isMobile) {
      setTimeout(() => {
        setIsSidePanelOpen(false);
      }, 200);
    }

    if (!path) {
      return;
    }

    navigate(path);
  };

  const handleSideBarBottomMenuClick = (item: any) => {
    const { key } =
      sideBarBottom[0].children.find((menu: any) => menu.key === item.key) ||
      {};

    if (key !== 'logout') {
      return;
    }

    clearUserSession();
    navigate(LINKS.LOGIN.build());
  };

  if (!isSignedIn || (isMobile && !isSidePanelOpen)) {
    return <></>;
  }

  return (
    <div className={classNames('sidebar-container')} ref={ref}>
      <Sider
        className={classNames('sidebar', {
          'sidebar-expanded': isSidePanelOpen,
        })}
        collapsible
        collapsed={!isSidePanelOpen}
        onCollapse={() => setIsSidePanelOpen(e => !e)}
      >
        <div className='logo' style={{ color: 'white', padding: '2rem' }}>
          <button
            className='hamburger-icon'
            aria-label='menu'
            onClick={() => {
              setIsSidePanelOpen(e => !e);
            }}
          >
            &#9776;
          </button>
          <span
            style={{ color: '#275747', fontWeight: 'bold', fontSize: '1.8rem' }}
          >
            {isSidePanelOpen && t('menu')}
          </span>
        </div>

        <FeelEatMenu
          theme='light'
          mode='inline'
          onClick={navigateToPage}
          className='sidebar-main-menu'
          selectedKeys={selectedKey}
          items={menus}
        />

        <FeelEatMenu
          className='sidebar-setting-menu'
          mode='vertical'
          items={sideBarBottom}
          selectedKeys={selectedKey}
          onClick={handleSideBarBottomMenuClick}
        />
      </Sider>
    </div>
  );
};

export default MainNavigation;
